import React, { useEffect, useState, useMemo } from "react";
import { Search, Plus, MoreHorizontal, Loader2, Copy, Edit, Trash, EyeIcon, CheckIcon, Delete, Eye, RefreshCw, Filter } from "lucide-react";
import { Input } from "../../../components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
// @ts-ignore
import "react-toastify/dist/ReactToastify.css";
import { Badge } from "../../../components/ui/badge";
import { useBlockchainRequest } from "../../Services/BlockchainRequest";
import { toast } from "react-toastify";
import { useUserContext } from "../../../Contexts/UserProvider";
import { Button } from "../../../components/ui/button";
import AddRequest from "../Products/AddRequest";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTrigger,
  DialogTitle,
} from "../../../components/ui/dialog";
import { ScrollArea } from "../../../components/ui/scroll-area";
import BlockchainDetailsPage from "./BlockchainDetailPage";
import ConfirmDialog from "../Modals/ConfirmationModal";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { useCatalogueManagement } from "../../Services/CatalogueManagement";
import FileViewerModal from "../FileViewer/FileViewerModal";
import DataTable from "react-data-table-component";
import { customStyles } from "../Common/DataTableStyles";

// Get status badge
const getStatusBadge = (status: string) => {
  switch (status) {
    case "Approved":
      return <Badge className="bg-green-100 text-green-800">Approved</Badge>
    case "InProgress":
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
          In progress
        </Badge>
      )
    case "Pending":
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
          Pending
        </Badge>
      )
    case "Rejected":
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
          Rejected
        </Badge>
      )
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

// Add missing components
function CheckCircle(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  )
}


const Approvals = () => {
  const [approvals, setApprovals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(null);
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [modalOpen, setModalOpen] = useState(false);
  const { tenant } = useUserContext();
  const [catalogueTypes, setCatalogueTypes] = useState([]);
  const { getRequests, requestApprove, requestReject, requestCancel } = useBlockchainRequest();
  const { getCatalogueTypes } = useCatalogueManagement();
  const [updateData, setUpdateData] = useState(null);
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const navigate = useNavigate();
  const [confirmationModal, setConfirmationModal] = useState({
    isShow: false,
    onConfirm: () => { },
  });
  const [orderDetailView, setOrderDetailView] = useState({
    data: null,
    isShow: false,
  });

  const columns = useMemo(() => [
    {
      name: "Request Item",
      selector: (row) => row?.itemName,
      sortable: true,
      grow: 2,
      minWidth: "200px",
    },
    {
      name: "Item Category",
      selector: (row) => row?.itemCatelogName,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Requested By",
      selector: (row) => row?.requestedFromTenantName,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Requested To",
      selector: (row) => row?.requestedToTenantName,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Requested Date",
      selector: (row) => row?.requestedDate,
      sortable: true,
      cell: (row) => dateConvertor(row?.requestedDate),
      minWidth: "150px",
    },
    {
      name: "Approved By",
      selector: (row) => row?.approvedBy,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Approved Date",
      selector: (row) => row?.approvedDate,
      sortable: true,
      cell: (row) => dateConvertor(row?.approvedDate),
      minWidth: "150px",
    },
    {
      name: "Status",
      selector: (row) => row?.registrationStatusValue,
      sortable: true,
      cell: (row) => getStatusBadge(row?.registrationStatusValue),
      minWidth: "120px",
    },
    {
      name: "Actions",
      cell: (row, index) => (
        <div className="flex justify-end w-full">
          <DropdownMenu modal={false} onOpenChange={(isOpen) => { setIsDropdownOpen(isOpen ? index : null) }} open={isDropdownOpen === index}>
            <DropdownMenuTrigger>
              <MoreHorizontal className="h-5 w-5 text-gray-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleOrderDetailView(row, true)}><EyeIcon className="h-4 w-4 mr-2" /> View</DropdownMenuItem>
              {(row?.registrationStatusValue === "InProgress" || row?.registrationStatusValue === "Pending") && (
                <>
                  <DropdownMenuItem onClick={() => handleUpdate(row)}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              {tenant?.id === row?.requestedToTenantId && (row?.registrationStatusValue === 'InProgress' || row?.registrationStatusValue === 'Pending') && (
                <DropdownMenuItem
                  onClick={() => {
                    setIsDropdownOpen(false);
                    handleConfirmationModal(true, () => handleApprove(row));
                  }}>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Approve
                </DropdownMenuItem>
              )}

              {tenant?.id === row?.requestedToTenantId && (row?.registrationStatusValue === 'InProgress' || row?.registrationStatusValue === 'Pending') && (
                <DropdownMenuItem
                  onClick={() => {
                    setIsDropdownOpen(false);
                    handleConfirmationModal(true, () => handleReject(row));
                  }} className="text-red-600">
                  <Trash className="h-4 w-4 mr-2" />
                  Reject
                </DropdownMenuItem>
              )}

              <DropdownMenuItem
                onClick={() => {
                  setIsDropdownOpen(false);
                  handleConfirmationModal(true, () => handleCancel(row));
                }}>
                <Delete className="h-4 w-4 mr-2" />
                Cancel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      right: true,
      minWidth: "100px",
    },
  ], [isDropdownOpen, tenant]);

  useEffect(() => {
    loadRequests();
    loadCatalogueTypes();
  }, []);

  const loadRequests = () => {
    setIsLoading(true);
    getRequests().then((res) => {
      setApprovals(res);
      setIsLoading(false);
    }).catch((err) => {
      console.error("Error fetching requests: ", err);
      setIsLoading(false);
    });
  }

  const handleApprove = (approvalDt) => {
    requestApprove(approvalDt?.id).then((res) => {
      if (res?.status !== 200) {
        toast.error("Request Approved Failed");
        return;
      }
      loadRequests();
      toast.success("Request Approved");
    }).catch((err) => {
      console.error("Error approving request: ", err);
      toast.error("Request Approved Failed");
    });
  }

  const handleReject = (approvalDt) => {
    requestReject(approvalDt?.id).then((res) => {
      if (res?.status !== 200) {
        toast.error("Request Rejected Failed");
        return;
      }
      loadRequests();
      toast.success("Request Rejected");
    }).catch((err) => {
      console.error("Error rejecting request: ", err);
      toast.error("Request Rejected Failed");
    });
  }

  const handleCancel = (approvalDt) => {
    requestCancel(approvalDt?.id).then((res) => {
      if (res?.status !== 200) {
        toast.error("Request Cancelled Failed");
        return;
      }
      loadRequests();
      toast.success("Request Cancelled");
    }).catch((err) => {
      console.error("Error cancelling request: ", err);
      toast.error("Request Cancelled Failed");
    });
  }

  const handleUpdate = (updateData) => {
    setIsDropdownOpen(false);
    setUpdateData(updateData);
    handleModalOpen(true);
  }

  const loadCatalogueTypes = () => {
    getCatalogueTypes(tenant?.id).then((response) => {
      setCatalogueTypes(response);
    }).catch((error) => {
      console.log("Error", error);
      toast.error("Failed to fetch Product Types");
    });
  }

  const dateConvertor = (value) => {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    const formatted = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    return formatted;
  }

  const handleModalOpen = (isOpen) => {
    setModalOpen(isOpen);
  }

  const handleOrderDetailView = (data, isOpen) => {
    navigate(`/app/${tenant?.code}/blockchain-explorer/${data?.serialNumber}?type=HASH&code=${tenant?.code}`);
  }

  const handleConfirmationModal = (isOpen, onConfirm) => {
    setConfirmationModal({
      isShow: isOpen,
      onConfirm: onConfirm,
    });
  };

  // Simulate refresh data
  const handleRefresh = () => {
    setIsLoading(true)
    setTimeout(() => {
      setIsLoading(false)
    }, 1000)
  }

  // Filter orders based on search query, status, and category
  const filteredOrders = approvals?.filter(
    (order) =>
      (order?.itemName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
        order?.requestedFromTenantName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
        order?.requestedFromTenantName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
        order?.invoiceNumber?.toLowerCase().includes(searchQuery?.toLowerCase())) &&
      (statusFilter === "all" || order?.registrationStatusValue === statusFilter) &&
      (categoryFilter === "all" || order?.itemCatelogName === categoryFilter),
  )

  return (
    <div className="w-full mx-auto p-6 space-y-6 order-management">
      <main className="container mx-auto p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Order Management{" "}
          </h1>
          <Button
            className="bg-primary hover:bg-primary/90"
            onClick={() => { handleModalOpen(true) }}
          >
            <Plus className="mr-2 h-4 w-4" /> Add New Order
          </Button>
        </div>
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search orders..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {catalogueTypes?.map((type, index) => (
                  <SelectItem key={index} value={type?.catalogName}>{type?.catalogName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isLoading}
              className={isLoading ? "opacity-50 cursor-not-allowed" : ""}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        <div className="bg-black rounded-lg shadow overflow-x-auto border border-gray-800">
          <DataTable
            columns={columns}
            data={filteredOrders}
            pagination
            progressPending={isLoading}
            progressComponent={
              <div className="flex items-center justify-center p-10">
                <Loader2 className="animate-spin h-8 w-8 text-white" />
              </div>
            }
            noDataComponent={
              <div className="text-center py-10 text-gray-400">
                No products found
              </div>
            }
            customStyles={customStyles}
            highlightOnHover
            pointerOnHover
            responsive
          />
        </div>

        <AddRequest
          isOpen={modalOpen}
          onClose={() => { handleModalOpen(false); setUpdateData(null) }}
          requestData={updateData}
          isUpdate={updateData ? true : false}
          onSuccessCallBack={() => { loadRequests() }}
        />

        <ConfirmDialog isShow={confirmationModal?.isShow} setIsDialogOpen={setConfirmationModal} title="Are you sure to continue ?" description="" confirmText="Confirm" cancelText="Cancel" onConfirm={confirmationModal?.onConfirm} />
        <FileViewerModal isShow={isFileViewerOpen} setIsShow={setIsFileViewerOpen} documents={updateData} />

        <Dialog open={orderDetailView?.isShow} onOpenChange={() => handleOrderDetailView(null, false)}>
          <DialogContent className="w-full max-w-7xl space-y-6 min-h-[500px] max-h-[650px] overflow-y-auto p-0">
            <ScrollArea>
              <BlockchainDetailsPage data={orderDetailView?.data} />
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default Approvals;
